/**
 * ConfirmDialog — styled replacement for native browser confirm().
 *
 * Usage:
 *   import { confirm } from '@/components/shared/ConfirmDialog'
 *
 *   const ok = await confirm('Delete this item?')
 *   if (ok) deleteItem()
 *
 *   // With options:
 *   const ok = await confirm('Deactivate patient?', {
 *     description: 'This action cannot be easily undone.',
 *     confirmLabel: 'Deactivate',
 *     variant: 'danger',
 *   })
 *
 *   // Two ways forward plus Cancel:
 *   const choice = await confirmChoice('Link a patient first?', {
 *     confirmLabel: 'Link a patient',
 *     alternateLabel: 'Export without patient',
 *   })  // 'confirm' | 'alternate' | 'cancel'
 */

import { useEffect, useRef, useCallback } from 'react'
import { createRoot } from 'react-dom/client'

interface ConfirmOptions {
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'danger'
}

export function confirm(message: string, options?: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    const cleanup = () => {
      root.unmount()
      container.remove()
    }

    const handleConfirm = () => { cleanup(); resolve(true) }
    const handleCancel = () => { cleanup(); resolve(false) }

    root.render(
      <ConfirmDialogUI
        message={message}
        description={options?.description}
        confirmLabel={options?.confirmLabel || 'Confirm'}
        cancelLabel={options?.cancelLabel || 'Cancel'}
        variant={options?.variant || 'default'}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    )
  })
}

export type ConfirmChoice = 'confirm' | 'alternate' | 'cancel'

/**
 * The same dialog with a second way forward beside the primary one. Resolves
 * which button was pressed; Escape and the backdrop are 'cancel'.
 */
export function confirmChoice(
  message: string,
  options: ConfirmOptions & { alternateLabel: string },
): Promise<ConfirmChoice> {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)

    const settle = (choice: ConfirmChoice) => {
      root.unmount()
      container.remove()
      resolve(choice)
    }

    root.render(
      <ConfirmDialogUI
        message={message}
        description={options.description}
        confirmLabel={options.confirmLabel || 'Confirm'}
        cancelLabel={options.cancelLabel || 'Cancel'}
        alternateLabel={options.alternateLabel}
        variant={options.variant || 'default'}
        onConfirm={() => settle('confirm')}
        onAlternate={() => settle('alternate')}
        onCancel={() => settle('cancel')}
      />
    )
  })
}

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// eslint-disable-next-line react-refresh/only-export-components
function ConfirmDialogUI({ message, description, confirmLabel, cancelLabel, alternateLabel, variant, onConfirm, onAlternate, onCancel }: {
  message: string
  description?: string
  confirmLabel: string
  cancelLabel: string
  alternateLabel?: string
  variant: 'default' | 'danger'
  onConfirm: () => void
  onAlternate?: () => void
  onCancel: () => void
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousFocus = useRef<HTMLElement | null>(null)

  // Store the previously focused element and restore on unmount
  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement
    return () => {
      previousFocus.current?.focus()
    }
  }, [])

  // Focus trap: keep Tab/Shift+Tab cycling within the dialog
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel()
      return
    }

    if (e.key !== 'Tab') return

    const dialog = dialogRef.current
    if (!dialog) return

    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }, [onCancel])

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40"
      onClick={onCancel}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={description ? "confirm-dialog-desc" : undefined}
        className="bg-surface rounded-xl shadow-2xl border border-border p-5 w-full max-w-sm mx-4 animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <p id="confirm-dialog-title" className="text-sm font-medium text-ink mb-1">{message}</p>
        {description && <p id="confirm-dialog-desc" className="text-xs text-ink-secondary mb-4">{description}</p>}
        {!description && <div className="mb-4" />}

        <div className="flex flex-wrap justify-end gap-2">
          <button
            onClick={onCancel}
            className="btn-secondary px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            autoFocus
          >
            {cancelLabel}
          </button>
          {alternateLabel && onAlternate && (
            <button
              onClick={onAlternate}
              className="btn-secondary px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            >
              {alternateLabel}
            </button>
          )}
          <button
            onClick={onConfirm}
            className={
              variant === 'danger'
                ? "px-3 py-1.5 text-xs font-medium text-white bg-error rounded-lg hover:bg-error/90 transition-colors"
                : "px-3 py-1.5 text-xs font-medium text-white bg-claro-600 rounded-lg hover:bg-claro-700 transition-colors"
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
